import React, { createContext, useContext, useState, useEffect } from 'react';
import { authService } from '../services/authService';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [registrationEnabled, setRegistrationEnabled] = useState(true);

  const isAuthenticated = !!token;

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if registration is enabled
        const regStatus = await authService.getRegistrationStatus();
        setRegistrationEnabled(regStatus);
        
        // If we have a token, try to validate it
        if (token) {
          // For now, we'll just check if token exists
          // In a real app, you might want to validate the token with the backend
          const username = localStorage.getItem('username');
          if (username) {
            setUser({ username });
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        logout();
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, [token]);

  const login = async (username, password) => {
    try {
      const response = await authService.login(username, password);
      const { token: newToken, username: userUsername } = response;
      
      setToken(newToken);
      setUser({ username: userUsername });
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('username', userUsername);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const register = async (username, password, email) => {
    try {
      const response = await authService.register(username, password, email);
      const { token: newToken, username: userUsername } = response;
      
      setToken(newToken);
      setUser({ username: userUsername });
      
      localStorage.setItem('token', newToken);
      localStorage.setItem('username', userUsername);
      
      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('username');
  };

  const value = {
    user,
    token,
    isAuthenticated,
    loading,
    registrationEnabled,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}; 